import React, {useEffect, useState} from 'react'
import api from '../../../api'
import ComponentButton from '../../components/ComponentButton'
import {useIntl} from 'react-intl'
import {TModuleComments} from './_types'
import {useAuth} from '../../../modules/auth'
import moment from 'moment'
import ComponentAttentionModal from '../../components/ComponentAttentionModal'

type TProps = TModuleComments & {
    embeddedContextId?: number | null
}

const ModuleComments: React.FC<TProps> = ({settings, embeddedContextId}) => {
    const intl = useIntl()
    const auth = useAuth()
    const {
        object = 'blogComments',
        foreign_key = 'blog_id',
        context_id = null,
        autoload = true,
        can_delete_comments,
        placeholder = intl.formatMessage({id: 'MODULE.COMMENTS.COMMENTS.PLACEHOLDER'}),
    } = settings

    const activeId = embeddedContextId ?? context_id ?? null

    const [items, setItems] = useState<Array<{ id: number; [k: string]: any }>>([])
    const [loading, setLoading] = useState(false)
    const [text, setText] = useState('')
    const [deleteCommentId, setDeleteCommentId] = useState<number | null>(null)

    const canLoad = autoload && !!activeId

    const load = async () => {
        if (!activeId) return
        setLoading(true)
        try {
            const res = await api(object, 'get', { [foreign_key]: activeId })
            setItems(Array.isArray(res?.data) ? res.data : [])
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { if (canLoad) load() }, [activeId])

    const submit = async () => {
        const val = text.trim()
        if (!activeId || !val) return
        await api(object, 'add', { [foreign_key]: activeId, comment: val, user_id: auth.currentUser?.id })
        setText('')
        await load()
    }

    if (!activeId) {
        return (
            <div className='card moduleComments'>
                <div className='card-body moduleComments_empty'>
                    {intl.formatMessage({ id: 'COMMENTS.NO_TARGET' })}
                </div>
            </div>
        )
    }

    return (
        <div className='card moduleComments'>
            <div className='card-body'>
                {/* форма ввода */}
                <div className='moduleComments_form'>
          <textarea
              className='form-control moduleComments_textarea'
              rows={3}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={placeholder}
          />
                    <div className='moduleComments_actions'>
                        <ComponentButton
                            type='custom'
                            settings={{ title: intl.formatMessage({ id: 'BUTTON.SEND' }), background: 'dark', icon: '' }}
                            customHandler={submit}
                        />
                    </div>
                </div>

                {/* список */}
                {loading ? (
                    <div className='moduleComments_spinner'>
                        <svg className='moduleComments_spinnerIcon' viewBox='0 0 50 50'>
                            <circle className='path' cx='25' cy='25' r='20' fill='none' strokeWidth='5'></circle>
                        </svg>
                    </div>
                ) : (
                    <div className='moduleComments_list'>
                        {items.map((comment) => (
                            <div key={comment.id} className='moduleComments_item'>
                                <div className='moduleComments_body'>
                                    <div className='moduleComments_meta'>
                                        {comment.user_id ? <span className='moduleComments_author'>{comment.user_id.title}</span> : null}
                                        {comment.created_at ? <span className='moduleComments_date'>{moment(comment.created_at).format('DD MMM YYYY г. HH:mm')}</span> : null}
                                    </div>
                                    <div className='moduleComments_text'>{comment.comment}</div>
                                    <div className='d-flex align-center justify-content-end'>
                                        {can_delete_comments && (
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary ms-2 p-0 d-flex align-items-center justify-content-center"
                                                style={{ width: "20px", height: "20px", cursor: "pointer" }}
                                                onClick={() => setDeleteCommentId(comment.id)}
                                            >
                                                <i className="bi bi-trash p-0"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <ComponentAttentionModal
                callback={deleteCommentId ? async () => {
                    await api(object, 'remove', { id: deleteCommentId })
                    await load()
                    setDeleteCommentId(null)
                } : null}
                resetCallback={() => setDeleteCommentId(null)}
            />
        </div>
    )
}

export default ModuleComments
